<template>
  <div ref="el" class="header-benefits" :class="`header-benefits--${color}`">
    <div
      v-for="(benefit, index) in resolvedBenefits"
      :key="index"
      class="header-benefits__item"
      :class="{
        'header-benefits__item--hiding': hidingBenefit === index,
        'header-benefits__item--showing': visibleBenefit === index,
      }"
    >
      <i class="far fa-check-circle header-benefits__item__icon" />
      <span v-html="benefit" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useInterval } from "@vueuse/core";
import { computed, ref } from "vue";

import { useI18nMainSiteLayout } from "@solvari/translations";

const { tr } = useI18nMainSiteLayout();

const props = withDefaults(
  defineProps<{
    benefits?: string[];
    color?: "gray" | "white";
  }>(),
  { color: "gray" },
);

const resolvedBenefits = computed(() => {
  return (
    props.benefits ?? [
      tr("main_site.layout.header_benefit_1"),
      tr("main_site.layout.header_benefit_2"),
      tr("main_site.layout.header_benefit_3"),
      tr("main_site.layout.header_benefit_4"),
    ]
  );
});

const visibleBenefit = ref<number | null>(0);
const hidingBenefit = ref<number | null>(null);

useInterval(6000, {
  callback: () => {
    hidingBenefit.value = visibleBenefit.value;
    visibleBenefit.value = null;

    setTimeout(() => {
      const current = hidingBenefit.value ?? 0;
      visibleBenefit.value = (current + 1) % resolvedBenefits.value.length;
      hidingBenefit.value = null;
    }, 300);
  },
});
</script>

<style lang="postcss">
.header-benefits {
  @apply flex justify-between gap-8;
  container-type: inline-size;

  &__item {
    @apply hidden w-full items-center justify-center text-center font-light transition-opacity ease-in-out;

    &__icon {
      @apply mb-0.5 me-2 mr-2;
    }

    &--hiding {
      @apply flex opacity-0;
    }

    &--showing {
      @apply flex opacity-100;
    }
  }

  &--gray {
    .header-benefits__item {
      @apply text-gray-600;

      &__icon {
        @apply text-success-600;
      }
    }
  }

  &--white {
    .header-benefits__item {
      @apply text-white/70;

      &__icon {
        @apply text-white;
      }
    }
  }

  @container (min-width: 800px) {
    .header-benefits__item {
      @apply w-auto text-sm opacity-100;

      &:not(:nth-child(4)) {
        @apply flex;
      }

      &:nth-child(4) {
        @apply hidden;
      }
    }
  }

  @container (min-width: 1000px) {
    .header-benefits__item {
      &:nth-child(4) {
        @apply flex;
      }
    }
  }
}
</style>
